import React, { type ComponentProps, useCallback, useEffect } from 'react';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { useReduceMotionStatus } from '../../../../hooks/useReduceMotionStatus';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pulses the opacity of a loading placeholder for as long as it is mounted.
 * Each `index` starts a little later, so a list of placeholders ripples.
 */
export const Pulse = (props: Props) => {
  const { index = 0, style, children, ...restProps } = props;

  const reduceMotionStatus = useReduceMotionStatus();

  // ─── State ─────────────────────────────────────────────────────────────────

  const pulseOpacity = useSharedValue(1);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const startPulse = useCallback(() => {
    if (reduceMotionStatus !== 'disabled') {
      cancelAnimation(pulseOpacity);
      pulseOpacity.value = 1;

      return;
    }

    pulseOpacity.value = withDelay(
      index * PULSE_STAGGER,
      withRepeat(withTiming(PULSE_MIN_OPACITY, PULSE_TIMING_CONFIG), -1, true),
    );
  }, [index, pulseOpacity, reduceMotionStatus]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(startPulse, [startPulse]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const pulseStyle = useAnimatedStyle(() => ({ opacity: pulseOpacity.value }));
  const containerStyles = [pulseStyle, style];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View {...restProps} style={containerStyles}>
      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PULSE_MIN_OPACITY = 0.5;
const PULSE_STAGGER = 120;
const PULSE_TIMING_CONFIG = { duration: 900, easing: Easing.inOut(Easing.sin) };

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  index?: number;
} & ComponentProps<typeof Animated.View>;
